import React from "react";
import { LogOut, Search, UserCheck } from "lucide-react";
import { Redirect, useHistory } from "react-router-dom";
import logo from "../../assets/logo.png";
import { controllerImpersonation } from "../../services/controllers/impersonation";
import { getSessionHomePath, isAdminSession, isImpersonating, logout } from "../../services/auth";
import { useImpersonation } from "../../store/impersonation/useImpersonation";
import {
  filterEntities,
  getEntityId,
  getEntityName,
  getUserDescription,
} from "../../components/impersonation/helpers";

function AdminList({ items, selected, onSelect, emptyText, itemDescription }) {
  return (
    <div className="h-[420px] min-h-0 overflow-y-auto rounded border border-zinc-200 bg-white md:h-[520px] xl:h-[600px]">
      {items.length === 0 && (
        <div className="px-4 py-5 text-sm text-zinc-500">{emptyText}</div>
      )}

      {items.map((item) => {
        const selectedItem = getEntityId(selected) === getEntityId(item);

        return (
          <button
            key={getEntityId(item)}
            type="button"
            onClick={() => onSelect(item)}
            data-selected={selectedItem}
            className="flex w-full flex-col items-start gap-1 border-b border-zinc-100 px-4 py-3 text-left transition last:border-b-0 hover:bg-scale-50 data-[selected=true]:bg-scale-100"
          >
            <span className="text-sm font-semibold text-zinc-800">{getEntityName(item)}</span>
            {itemDescription?.(item) && (
              <span className="text-xs text-zinc-500">{itemDescription(item)}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function SearchField({ id, label, value, onChange, placeholder, disabled }) {
  return (
    <div className="flex flex-col gap-2">
      <label className="text-sm font-semibold text-zinc-700" htmlFor={id}>
        {label}
      </label>
      <div className="flex items-center gap-2 rounded border border-zinc-200 bg-white px-3 py-2">
        <Search className="h-4 w-4 text-zinc-400" />
        <input
          id={id}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="w-full border-0 text-sm outline-none disabled:bg-white"
          placeholder={placeholder}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

export default function AdminImpersonation() {
  const history = useHistory();
  const CLImpersonation = controllerImpersonation();
  const {
    loading,
    starting,
    stores,
    users,
    storeFilter,
    userFilter,
    selectedStore,
    selectedUser,
  } = useImpersonation((st) => st.data);
  const clearFlow = useImpersonation((st) => st.actions.clearFlow);
  const hasAdminAccess = isAdminSession() && !isImpersonating();

  React.useEffect(() => {
    if (!hasAdminAccess) return;

    clearFlow();
    CLImpersonation.loadStores();
  }, [hasAdminAccess]);

  const filteredStores = filterEntities(stores, storeFilter);
  const filteredUsers = filterEntities(users, userFilter);

  if (!hasAdminAccess) {
    return <Redirect to={getSessionHomePath()} />;
  }

  const handleLogout = () => {
    logout();
    history.push("/");
  };

  return (
    <div className="flex min-h-screen flex-col bg-zinc-50">
      <header className="flex h-20 items-center justify-between border-b border-zinc-200 bg-white px-6 shadow-sm">
        <img src={logo} alt="Petland" title="Petland" className="h-10 w-auto" />
        <button
          type="button"
          onClick={handleLogout}
          className="inline-flex items-center gap-2 rounded border border-zinc-200 px-3 py-2 text-sm font-semibold text-zinc-600 transition hover:bg-zinc-100"
        >
          <LogOut className="h-4 w-4" />
          Sair
        </button>
      </header>

      <main className="flex flex-1 flex-col gap-6 p-6">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-900">Selecionar acesso</h1>
          <p className="mt-1 text-sm text-zinc-500">
            Escolha uma loja e um usuário para acessar o sistema com a visão operacional correspondente.
          </p>
        </div>

        <section className="grid h-fit gap-6 lg:grid-cols-2">
          <div className="flex min-h-[300px] flex-col gap-3">
            <SearchField
              id="admin-impersonation-store"
              label="Loja"
              value={storeFilter}
              onChange={CLImpersonation.setStoreFilter}
              placeholder="Buscar loja"
            />
            <AdminList
              items={filteredStores}
              selected={selectedStore}
              onSelect={CLImpersonation.selectStore}
              emptyText={loading ? "Carregando lojas..." : "Nenhuma loja encontrada."}
              itemDescription={(store) => store?.cnpj || store?.city || store?.location?.city}
            />
          </div>

          <div className="flex min-h-[300px] flex-col gap-3">
            <SearchField
              id="admin-impersonation-user"
              label="Usuário"
              value={userFilter}
              onChange={CLImpersonation.setUserFilter}
              placeholder="Buscar usuário"
              disabled={!selectedStore}
            />
            <AdminList
              items={filteredUsers}
              selected={selectedUser}
              onSelect={CLImpersonation.selectUser}
              emptyText={
                selectedStore
                  ? loading ? "Carregando usuários..." : "Nenhum usuário encontrado."
                  : "Selecione uma loja primeiro."
              }
              itemDescription={getUserDescription}
            />
          </div>
        </section>

        <footer className="flex flex-col gap-3 border-t border-zinc-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-zinc-600">
            {selectedStore && selectedUser
              ? `${getEntityName(selectedUser)} em ${getEntityName(selectedStore)}`
              : "Nenhuma sessão de loja foi iniciada."}
          </div>
          <button
            type="button"
            onClick={CLImpersonation.start}
            disabled={!selectedStore || !selectedUser || starting}
            className="inline-flex items-center justify-center gap-2 rounded bg-scale-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-scale-600 disabled:cursor-not-allowed disabled:bg-zinc-300"
          >
            <UserCheck className="h-4 w-4" />
            {starting ? "Iniciando sessão..." : "Iniciar sessão"}
          </button>
        </footer>
      </main>
    </div>
  );
}
